import { createAction, createReducer } from '@reduxjs/toolkit';

import {
  type Carrier,
  type RouterNetworkChangedData,
  type RouterNetworkState,
  type RouterNetworkStatusItem,
} from '@okvc/schema';

export interface RouterNetworkStatus {
  state: RouterNetworkState;
  carrier?: Carrier;
  onPrimary: boolean;
  testing: boolean;
  changedAt: string;
}

export interface RouterNetworkByPatientState {
  byPatientId: Record<string, RouterNetworkStatus>;
}

const initialState: RouterNetworkByPatientState = { byPatientId: {} };
const NEVER_CHANGED = new Date(0).toISOString();

export const routerNetworkChanged = createAction<RouterNetworkChangedData>('routerNetwork/changed');
export const routerNetworkHydrated = createAction<RouterNetworkStatusItem[]>('routerNetwork/hydrated');

function testingAfter(change: RouterNetworkChangedData, previous: RouterNetworkStatus | undefined): boolean {
  if (change.type === 'capacity_test_started') {
    return true;
  }
  if (change.type === 'capacity_test_finished') {
    return false;
  }
  return previous?.testing ?? false;
}

function carrierAfter(change: RouterNetworkChangedData): Carrier | undefined {
  if (change.type === 'switch_failed') {
    return change.fromCarrier;
  }
  return change.toCarrier ?? change.fromCarrier;
}

function isNewer(item: RouterNetworkStatusItem, previous: RouterNetworkStatus | undefined): boolean {
  return !previous || (item.changedAt ?? NEVER_CHANGED) > previous.changedAt;
}

const routerNetworkReducer = createReducer(initialState, (builder) => {
  builder.addCase(routerNetworkChanged, (state, action) => {
    const change = action.payload;
    const previous = state.byPatientId[change.patientId];
    state.byPatientId[change.patientId] = {
      state: change.state,
      carrier: carrierAfter(change),
      onPrimary: change.onPrimary,
      testing: testingAfter(change, previous),
      changedAt: new Date().toISOString(),
    };
  });
  builder.addCase(routerNetworkHydrated, (state, action) => {
    for (const item of action.payload) {
      if ((item.state || item.testing) && isNewer(item, state.byPatientId[item.patientId])) {
        state.byPatientId[item.patientId] = {
          state: item.state ?? 'online',
          carrier: item.carrier ?? undefined,
          onPrimary: item.onPrimary,
          testing: item.testing,
          changedAt: item.changedAt ?? NEVER_CHANGED,
        };
      }
    }
  });
});

export default routerNetworkReducer;
